import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button as MuiButton, Grid, Stack, Typography, useTheme } from "@mui/material";
import { AddCircleOutlineOutlined, AttachMoneyOutlined, CancelOutlined, DeleteOutlineOutlined, DoDisturbAltOutlined, Restore } from "@mui/icons-material";
import { useNavigation } from "../../../hooks/useNavigation";
import dayjs from "dayjs";
import CustomTable from "../../common/CustomTable";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import CustomIconButton from "../../common/CustomIconButton";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import DatePickerPopup from "../../common/DatePickerPopup";
import { useModalStyles } from "../../common/useModalStyles";
import { tokens } from "../../../theme";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  deleteProductPurchasePriceObj,
  fetchActiveProductPurchasePrices,
  inactivateProductPurchasePriceItem,
  initialValuesOfProductPurchasePrice,
  ProductItemInfo,
  ProductPurchasePriceInfo,
  saveAllProductItemsPurchasePrices,
  saveProductPurchasePriceItem,
  useProductReducer,
} from "../../../redux/slice/product/createProductSlice";
import { setSingleAlertObj } from "../../../redux/slice/commonSlice";
import ProductCogsPopup from "./ProductCogsPopup";
import ProductCogsHistoryPopup from "./ProductCogsHistoryPopup";
import ProductItemsCogsPopup from "./ProductItemsCogsPopup";
import Button from "../../common/Button";

type Column = {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
};

interface ProductCogsSectionProps {
  productId: number;
  hasSKUPrice: boolean;
  productItems: ProductItemInfo[];
  isDraftMode?: boolean;
  draftPurchasePrices?: ProductPurchasePriceInfo[];
  onDraftPurchasePricesChange?: (prices: ProductPurchasePriceInfo[]) => void;
  embedded?: boolean;
}

const ProductCogsSection: React.FC<ProductCogsSectionProps> = ({
  productId,
  hasSKUPrice,
  productItems,
  isDraftMode = false,
  draftPurchasePrices = [],
  onDraftPurchasePricesChange,
  embedded = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { tokens: modalTokens, iconBoxSx, cancelBtnSx } = useModalStyles();
  // Matches the OrderList table look — cyan headers, subtle row dividers, teal hover.
  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listHoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";
  const productTableSx = {
    border: `1px solid ${modalTokens.border}`,
    borderRadius: "10px",
    overflow: "hidden",
    p: 0,
    "& .MuiTableContainer-root": { borderRadius: "10px", border: "none", backgroundColor: "transparent" },
    "& table": {  borderCollapse: "collapse", borderSpacing: 0 },
    "& .MuiTableCell-root": { borderBottom: `1px solid ${listBorderColor}`, py: 1.15, fontSize: "0.84rem" },
    "& .MuiTableHead-root .MuiTableCell-root": { height: "42px"  },
    "& .MuiTableHead-root .MuiTypography-root": { color: "#00B8D9 !important", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.02em" },
    "& .MuiTableBody-root .MuiTableRow-root": { transition: "background-color 0.18s ease, box-shadow 0.18s ease" },
    "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: `${listHoverBg} !important` },
    "& .MuiTableBody-root .MuiTableCell-root": { color: modalTokens.textPrimary, fontSize: "0.84rem" },
    "& .MuiTableBody-root .MuiTypography-root": { fontSize: "0.84rem" },
    "& .MuiIconButton-root": { color: isDark ? "#94a3b8" : "#475569", borderRadius: "8px", "&:hover": { color: modalTokens.accent, backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)" } },
    "& .MuiPaginationItem-root": { borderColor: listBorderColor, color: modalTokens.textSecondary, borderRadius: "8px", "&.Mui-selected": { color: "#fff", backgroundColor: modalTokens.accent, borderColor: modalTokens.accent } },
  };
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const {
    productPurchasePrices,
    saveProductPurchasePriceStatus,
    inactivateProductPurchasePriceStatus,
    deleteProductPurchasePriceStatus,
  } = useProductReducer();

  const [showCogsPopup, setShowCogsPopup] = useState(false);
  const [showItemsCogsPopup, setShowItemsCogsPopup] = useState(false);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);
  const [inactiveOnly, setInactiveOnly] = useState(false);
  const [cogsObject, setCogsObject] = useState<ProductPurchasePriceInfo>(initialValuesOfProductPurchasePrice);
  const [optionPopupShow, setOptionPopupShow] = useState<OptionPopupObjectProps>({ message: "", show: false });
  const [datePickerPopupObj, setDatePickerPopupObj] = useState({
    label: "",
    title: "",
    open: false,
    validationArray: [] as any[],
    submitDate: (_date: string) => {},
  });

  useEffect(() => {
    if (!isDraftMode && productId > 0) {
      dispatch(fetchActiveProductPurchasePrices({ productId }));
    }
  }, [productId, dispatch, isDraftMode]);

  useEffect(() => {
    if (isDraftMode) return;
    if (saveProductPurchasePriceStatus === "success") {
      dispatch(setSingleAlertObj({ message: "COGs saved successfully.", alertType: "success" }));
      setShowCogsPopup(false);
      setShowItemsCogsPopup(false);
      setInactiveOnly(false);
    }
  }, [saveProductPurchasePriceStatus, dispatch, isDraftMode]);

  useEffect(() => {
    if (isDraftMode) return;
    if (inactivateProductPurchasePriceStatus === "success") {
      dispatch(setSingleAlertObj({ message: "COGs inactivated successfully.", alertType: "success" }));
      setShowCogsPopup(false);
      setInactiveOnly(false);
    }
  }, [inactivateProductPurchasePriceStatus, dispatch, isDraftMode]);

  useEffect(() => {
    if (isDraftMode) return;
    if (deleteProductPurchasePriceStatus === "success") {
      dispatch(setSingleAlertObj({ message: "COGs deleted successfully.", alertType: "success" }));
    }
  }, [deleteProductPurchasePriceStatus, dispatch, isDraftMode]);

  const activePurchasePrices = isDraftMode ? draftPurchasePrices : productPurchasePrices;

  const filteredPrices = useMemo(() => {
    return (activePurchasePrices || []).filter((price: ProductPurchasePriceInfo) => {
      const itemId = price.productItemId ?? price.productItem?.productItemId;
      const sku = price.sku || price.productItem?.sku;
      return hasSKUPrice ? !!(itemId || sku) : !itemId && !sku;
    });
  }, [activePurchasePrices, hasSKUPrice]);

  const showInactiveDateColumn = useMemo(() => {
    return filteredPrices.some((price: ProductPurchasePriceInfo) => {
      const inactiveDate = price.inactiveDate;
      return inactiveDate && inactiveDate !== "-" && dayjs(inactiveDate).isValid();
    });
  }, [filteredPrices]);

  const tableRows = useMemo(() => {
    return filteredPrices.map((obj: ProductPurchasePriceInfo, index: number) => ({
      ...obj,
      rowIndex: index,
      sku: obj.sku || obj.productItem?.sku || "-",
      ...(showInactiveDateColumn ? { inactiveDate: obj.inactiveDate || "-" } : {}),
      purchasePrice: <CustomCurrencyField externalValue={obj.purchasePrice} displayType="text" />,
    }));
  }, [filteredPrices, showInactiveDateColumn]);

  const columns: Column[] = useMemo(() => {
    const cols: Column[] = [{ id: "effectiveDate", label: "Effective Date", minWidth: 100 }];
    if (hasSKUPrice) {
      cols.push({ id: "sku", label: "SKU", minWidth: 120 });
    }
    cols.push({ id: "purchasePrice", label: "Purchase Price", minWidth: 120 });
    if (showInactiveDateColumn) {
      cols.push({ id: "inactiveDate", label: "Inactive Date", minWidth: 100 });
    }
    cols.push({ id: "action", label: "Action", minWidth: 100 });
    return cols;
  }, [hasSKUPrice, showInactiveDateColumn]);

  const legacyItems = [
    { icon: <DeleteOutlineOutlined color="error" />, text: "Delete" },
    { icon: <DoDisturbAltOutlined color="warning" />, text: "Inactivate" },
  ];

  const actionButtonSx = {
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: "0.82rem",
    borderRadius: "8px",
    px: 1.6,
    py: 0.8,
    color: "#fff",
    backgroundColor: "#3b82f6",
    "&:hover": { backgroundColor: "#2563eb" },
  };

  const openAddPopup = () => {
    if (isDraftMode && hasSKUPrice && (!productItems || productItems.length === 0)) {
      dispatch(setSingleAlertObj({
        title: "warning",
        message: "Please add at least one product item to add COGs.",
        alertType: "warning",
      }));
      return;
    }
    setCogsObject({ ...initialValuesOfProductPurchasePrice, productId });
    setInactiveOnly(false);
    if (hasSKUPrice) {
      setShowItemsCogsPopup(true);
    } else {
      setShowCogsPopup(true);
    }
  };

  const updateDraftPrices = (prices: ProductPurchasePriceInfo[]) => {
    onDraftPurchasePricesChange?.(prices);
  };

  const handleSaveCogs = (data: ProductPurchasePriceInfo) => {
    if (isDraftMode) {
      if (inactiveOnly) {
        const updated = draftPurchasePrices.map((price) =>
          price === cogsObject ? { ...price, inactiveDate: data.inactiveDate } : price
        );
        updateDraftPrices(updated);
        setShowCogsPopup(false);
        setInactiveOnly(false);
        return;
      }
      const newEntry = { ...data, productId: 0, productPurchasePriceId: 0 };
      updateDraftPrices([...draftPurchasePrices, newEntry]);
      setShowCogsPopup(false);
      return;
    }

    if (inactiveOnly) {
      dispatch(inactivateProductPurchasePriceItem({
        productPurchasePriceId: data.productPurchasePriceId,
        date: data.inactiveDate,
      }));
      return;
    }
    dispatch(saveProductPurchasePriceItem({ purchasePrice: { ...data, productId } }));
  };

  const handleSaveItemsCogs = (items: ProductPurchasePriceInfo[]) => {
    if (isDraftMode) {
      const newEntries = items.map((item) => ({
        ...item,
        productId: 0,
        productPurchasePriceId: 0,
      }));
      updateDraftPrices([...draftPurchasePrices, ...newEntries]);
      setShowItemsCogsPopup(false);
      return;
    }
    dispatch(saveAllProductItemsPurchasePrices({
      purchasePriceItems: { productId, productItemPurchasePrices: items },
    }));
  };

  const onDeleteClick = useCallback((row: ProductPurchasePriceInfo & { rowIndex?: number }) => {
    setOptionPopupShow({
      message: "Do you really want to delete this COGs record?",
      show: true,
      variant: "custom",
      title: "Confirm",
      buttons: [
        {
          name: "YES",
          color: "primary",
          onClick: () => {
            setOptionPopupShow({ message: "", show: false });
            if (isDraftMode) {
              const updated = draftPurchasePrices.filter((_, index) => index !== row.rowIndex);
              updateDraftPrices(updated);
              return;
            }
            dispatch(deleteProductPurchasePriceObj({ productPurchasePriceId: row.productPurchasePriceId }));
          },
        },
        { name: "NO", color: "secondary", onClick: () => setOptionPopupShow({ message: "", show: false }) },
      ],
    });
  }, [dispatch, isDraftMode, draftPurchasePrices, onDraftPurchasePricesChange]);

  const onInactivateClick = useCallback((row: ProductPurchasePriceInfo & { rowIndex?: number }) => {
    setDatePickerPopupObj({
      open: true,
      title: "Set Inactive Date",
      label: "",
      validationArray: [],
      submitDate: (date: string) => {
        if (isDraftMode) {
          const updated = draftPurchasePrices.map((price, index) =>
            index === row.rowIndex ? { ...price, inactiveDate: date } : price
          );
          updateDraftPrices(updated);
        } else {
          dispatch(inactivateProductPurchasePriceItem({
            productPurchasePriceId: row.productPurchasePriceId,
            date,
          }));
        }
        setDatePickerPopupObj({ label: "", title: "", open: false, validationArray: [], submitDate: () => {} });
      },
    });
  }, [dispatch, isDraftMode, draftPurchasePrices, onDraftPurchasePricesChange]);

  const actionComponent = useCallback((row: ProductPurchasePriceInfo & { rowIndex?: number }) => (
    <>
      <CustomIconButton
        color="warning"
        size="small"
        variant="outlined"
        onClick={() => onInactivateClick(row)}
        tooltipMessage="Inactivate"
        tooltipPlacement="bottom"
        sx={{ mr: 1 }}
      >
        <DoDisturbAltOutlined fontSize="small" />
      </CustomIconButton>
      <CustomIconButton
        color="error"
        size="small"
        variant="outlined"
        onClick={() => onDeleteClick(row)}
        tooltipMessage="Delete"
        tooltipPlacement="bottom"
      >
        <DeleteOutlineOutlined fontSize="small" />
      </CustomIconButton>
    </>
  ), [onDeleteClick, onInactivateClick]);

  return (
    <>
      <Box
        sx={
          embedded
            ? {
                borderRadius: "12px",
                border: `1px solid ${modalTokens.border}`,
                backgroundColor: modalTokens.bg,
                p: { xs: 2, sm: 2.5 },
              }
            : { mt: 3 }
        }
      >
        {embedded ? (
          <Box sx={{ mb: 2.5 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box sx={iconBoxSx(modalTokens.accent)}>
                  <AttachMoneyOutlined sx={{ fontSize: 20, color: modalTokens.accent }} />
                </Box>
                <Typography sx={{ fontSize: "1.15rem", fontWeight: 800, color: modalTokens.textPrimary, letterSpacing: "-0.01em" }}>
                  Product COGS
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={8}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                
                {/* <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 0.75, py: 0.4 }}>
                  <DeleteOutlineOutlined sx={{ fontSize: 16, color: "#ef4444" }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#ef4444" }}>Delete</Typography>
                </Box>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 0.75, py: 0.4 }}>
                  <DoDisturbAltOutlined sx={{ fontSize: 16, color: "#f59e0b" }} />
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#f59e0b" }}>Inactivate</Typography>
                </Box> */}
                {!isDraftMode && (
                  <MuiButton
                    type="button"
                    variant="contained"
                    disableElevation
                    startIcon={<Restore sx={{ fontSize: "1rem !important" }} />}
                    onClick={() => setShowHistoryPopup(true)}
                    sx={actionButtonSx}
                  >
                    COGS History
                  </MuiButton>
                )}
                <MuiButton
                  type="button"
                  variant="contained"
                  disableElevation
                  startIcon={<AddCircleOutlineOutlined sx={{ fontSize: "1rem !important" }} />}
                  onClick={openAddPopup}
                  sx={actionButtonSx}
                >
                  {hasSKUPrice ? "Add Item COGS" : "Add COGS"}
                </MuiButton>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1,
              mb: 2,
              borderRadius: 1,
              boxShadow: 1,
              p: 1,
              backgroundColor: colors.grey[900],
            }}
          >
            {legacyItems.map((item) => (
              <Box key={item.text} sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 0.75, py: 0.4 }}>
                {item.icon}
                <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>{item.text}</Typography>
              </Box>
            ))}
            {!isDraftMode && (
              <MuiButton
                type="button"
                variant="contained"
                disableElevation
                startIcon={<Restore sx={{ fontSize: "1rem !important" }} />}
                onClick={() => setShowHistoryPopup(true)}
                sx={actionButtonSx}
              >
                COGS History
              </MuiButton>
            )}
            <MuiButton
              type="button"
              variant="contained"
              disableElevation
              startIcon={<AddCircleOutlineOutlined sx={{ fontSize: "1rem !important" }} />}
              onClick={openAddPopup}
              sx={actionButtonSx}
            >
              {hasSKUPrice ? "Add Item COGS" : "Add COGS"}
            </MuiButton>
          </Box>
        )}

        <CustomTable
          columns={columns}
          rows={tableRows}
          actionComponent={(row: ProductPurchasePriceInfo & { rowIndex?: number }) => actionComponent(row)}
          disablePagination
          borderBox={!embedded}
          emptyText="No Product COGS record(s) found"
          showMenu
          sx={embedded ? productTableSx : { pl: { xs: 0.5, sm: 1 } }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1 }}>
          <Button
            color="error"
            variant="outlined"
            startIcon={<CancelOutlined sx={{ fontSize: "1rem !important" }} />}
            onClick={() => navigate("/products/explore_market")}
            sx={{
              ...cancelBtnSx,
              fontSize: "0.78rem",
              py: 1,
              px: 1.5,
              minWidth: "auto",
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      <ProductCogsPopup
        open={showCogsPopup}
        onClose={() => { setShowCogsPopup(false); setInactiveOnly(false); }}
        onSave={handleSaveCogs}
        purchasePriceObject={cogsObject}
        inactiveOnly={inactiveOnly}
      />
      <ProductItemsCogsPopup
        open={showItemsCogsPopup}
        onClose={() => setShowItemsCogsPopup(false)}
        onSave={handleSaveItemsCogs}
        productId={productId}
        productItemsList={productItems}
      />
      {!isDraftMode && (
        <ProductCogsHistoryPopup
          open={showHistoryPopup}
          onClose={() => setShowHistoryPopup(false)}
          productId={productId}
          hasSKUPrice={hasSKUPrice}
        />
      )}
      {optionPopupShow.show && (
        <OptionsPopup
          open={optionPopupShow.show}
          onClose={() => setOptionPopupShow({ message: "", show: false })}
          variant={optionPopupShow.variant}
          message={<>{optionPopupShow.message}</>}
          buttons={optionPopupShow.buttons}
          title={optionPopupShow.title}
        />
      )}
      {datePickerPopupObj.open && (
        <DatePickerPopup
          open={datePickerPopupObj.open}
          title={datePickerPopupObj.title}
          label={datePickerPopupObj.label}
          submitDate={(date: string) => datePickerPopupObj.submitDate(date)}
          validationArray={datePickerPopupObj.validationArray}
          onClose={() => setDatePickerPopupObj({ label: "", title: "", open: false, validationArray: [], submitDate: () => {} })}
        />
      )}
    </>
  );
};

export default ProductCogsSection;
